/**
 * Review Submission Widget
 * 
 * Widget 2: Post-Purchase Collection Widget
 * 
 * Features:
 * 1. Purchase verification before showing form
 * 2. Multi-dimensional star ratings
 * 3. Review text and title input
 * 4. Image upload (max 3)
 * 5. Submit with order verification
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Star,
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  Package,
  DollarSign,
  Truck,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Interactive Star Rating Input
const StarRatingInput = ({ value, onChange, label, icon: Icon, required = false }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-gray-500" />}
        <Label className="text-sm">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-7 w-7 cursor-pointer transition-all ${
              star <= (hover || value) 
                ? 'text-yellow-400 fill-yellow-400 scale-110' 
                : 'text-gray-300 hover:text-yellow-200'
            }`}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(star)}
          />
        ))}
        {value > 0 && (
          <span className="ml-2 text-sm text-gray-500">
            {value === 1 && 'Poor'}
            {value === 2 && 'Fair'}
            {value === 3 && 'Good'}
            {value === 4 && 'Very Good'}
            {value === 5 && 'Excellent'}
          </span>
        )}
      </div>
    </div>
  );
};

// Main Review Submission Widget
const ReviewSubmissionWidget = ({ 
  productId, 
  orderId, 
  productTitle,
  productImage,
  onSuccess 
}) => {
  const [verificationStatus, setVerificationStatus] = useState('checking'); // checking, verified, not_verified, already_reviewed
  const [verificationMessage, setVerificationMessage] = useState('');
  const [existingReviewId, setExistingReviewId] = useState(null);
  
  // Form state
  const [ratings, setRatings] = useState({
    overall: 0,
    quality: 0,
    value: 0,
    delivery: 0
  });
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const { user, apiCall, token } = useAuth();
  const { toast } = useToast();
  
  // Verify purchase eligibility
  const verifyPurchase = useCallback(async () => {
    if (!productId || !orderId) {
      setVerificationStatus('not_verified');
      setVerificationMessage('Product or order information missing');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        order_id: orderId,
        product_id: productId
      });
      
      if (user?.id) {
        params.append('user_id', user.id);
      }
      if (user?.email) {
        params.append('user_email', user.email);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/reviews/verify_purchase?${params}`);
      const data = await response.json();
      
      if (data.can_review) {
        setVerificationStatus('verified');
        setVerificationMessage('Purchase verified! You can submit a review.');
      } else if (data.existing_review_id) {
        setVerificationStatus('already_reviewed');
        setVerificationMessage(data.reason || 'You have already reviewed this product.');
        setExistingReviewId(data.existing_review_id);
      } else {
        setVerificationStatus('not_verified');
        setVerificationMessage(data.reason || 'Unable to verify purchase.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('not_verified');
      setVerificationMessage('Unable to verify purchase. Please try again later.');
    }
  }, [productId, orderId, user]);
  
  useEffect(() => {
    verifyPurchase();
  }, [verifyPurchase]);
  
  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (images.length + files.length > 3) {
      toast({
        title: 'Maximum 3 images',
        description: 'You can upload up to 3 images per review.',
        variant: 'destructive'
      });
      return;
    }
    
    setUploading(true);
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: `${file.name} exceeds 5MB limit.`,
          variant: 'destructive'
        });
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('upload_type', 'review');
        
        const response = await fetch(`${BACKEND_URL}/api/files/upload`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setImages(prev => [...prev, data.url || data.file_url]);
        } else {
          throw new Error('Upload failed');
        }
      } catch (error) {
        toast({
          title: 'Upload failed',
          description: `Failed to upload ${file.name}`,
          variant: 'destructive'
        });
      }
    }
    
    setUploading(false);
  };
  
  // Remove image
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };
  
  // Submit review
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (ratings.overall === 0) {
      toast({
        title: 'Rating required',
        description: 'Please provide an overall rating.',
        variant: 'destructive'
      });
      return;
    }
    
    setSubmitting(true);
    
    try {
      const reviewData = {
        order_id: orderId,
        product_id: productId,
        rating_overall: ratings.overall,
        rating_quality: ratings.quality || null,
        rating_value: ratings.value || null,
        rating_delivery: ratings.delivery || null,
        review_title: reviewTitle || null,
        review_text: reviewText || null,
        review_images: images,
        customer_name: user?.name,
        customer_email: user?.email
      };
      
      // Use authenticated endpoint if user is logged in
      const endpoint = user?.id 
        ? '/api/reviews/submit/authenticated'
        : '/api/reviews/submit';
      
      let response;
      if (user?.id && apiCall) {
        response = await apiCall(endpoint, {
          method: 'POST',
          body: JSON.stringify(reviewData)
        });
      } else {
        // Public submission (requires vendor_id)
        // Need to get vendor_id from the order or product
        const orderResponse = await fetch(`${BACKEND_URL}/api/orders/${orderId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        let vendorId = null;
        if (orderResponse.ok) {
          const orderData = await orderResponse.json();
          vendorId = orderData.vendor_id;
        }
        
        if (!vendorId) {
          // Try to get from listing
          const listingResponse = await fetch(`${BACKEND_URL}/api/listings/${productId}`);
          if (listingResponse.ok) {
            const listingData = await listingResponse.json();
            vendorId = listingData.vendor_id;
          }
        }
        
        const publicResponse = await fetch(`${BACKEND_URL}/api/reviews/submit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...reviewData,
            vendor_id: vendorId
          })
        });
        
        if (!publicResponse.ok) {
          throw new Error('Failed to submit review');
        }
        
        response = await publicResponse.json();
      }
      
      setSubmitted(true);
      toast({
        title: '🎉 Review submitted!',
        description: 'Thank you for your feedback!'
      });
      
      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      toast({
        title: 'Submission failed',
        description: error.message || 'Failed to submit review. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Verification checking state
  if (verificationStatus === 'checking') {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Verifying purchase...</span>
        </CardContent>
      </Card>
    );
  }
  
  // Already reviewed state
  if (verificationStatus === 'already_reviewed') {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
          <h3 className="font-semibold text-lg mb-2">Review Already Submitted</h3>
          <p className="text-gray-500">{verificationMessage}</p>
        </CardContent>
      </Card>
    );
  }
  
  // Not verified state
  if (verificationStatus === 'not_verified') {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h3 className="font-semibold text-lg mb-2">Unable to Verify Purchase</h3>
          <p className="text-gray-500">{verificationMessage}</p>
        </CardContent>
      </Card>
    );
  }
  
  // Submitted state
  if (submitted) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
          <h3 className="font-semibold text-lg mb-2">Thank You!</h3>
          <p className="text-gray-500">Your review has been submitted successfully.</p>
        </CardContent>
      </Card>
    );
  }
  
  // Verified - show form
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-4">
          {productImage && (
            <img 
              src={productImage} 
              alt={productTitle}
              className="w-16 h-16 object-cover rounded-lg border"
            />
          )}
          <div className="flex-1">
            <CardTitle>Write a Review</CardTitle>
            <CardDescription>
              {productTitle && <span className="block">{productTitle}</span>}
              <Badge variant="outline" className="mt-2 text-green-600 border-green-300">
                <ShieldCheck className="h-3 w-3 mr-1" />
                Verified Purchase
              </Badge>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Overall Rating - Required */}
          <StarRatingInput
            value={ratings.overall}
            onChange={(v) => setRatings(prev => ({ ...prev, overall: v }))}
            label="Overall Rating"
            icon={Star}
            required
          />
          
          {/* Additional Ratings */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <StarRatingInput
              value={ratings.quality}
              onChange={(v) => setRatings(prev => ({ ...prev, quality: v }))}
              label="Quality"
              icon={Package}
            />
            <StarRatingInput
              value={ratings.value}
              onChange={(v) => setRatings(prev => ({ ...prev, value: v }))}
              label="Value"
              icon={DollarSign}
            />
            <StarRatingInput
              value={ratings.delivery}
              onChange={(v) => setRatings(prev => ({ ...prev, delivery: v }))}
              label="Delivery"
              icon={Truck}
            />
          </div>
          
          {/* Review Title */}
          <div className="space-y-2">
            <Label>Review Title</Label>
            <Input
              value={reviewTitle}
              onChange={(e) => setReviewTitle(e.target.value)}
              placeholder="Summarize your experience..."
              maxLength={100}
            />
          </div>
          
          {/* Review Text */}
          <div className="space-y-2">
            <Label>Your Review</Label>
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience with this product..."
              rows={5}
              maxLength={2000}
            />
            <p className="text-xs text-gray-400 text-right">{reviewText.length}/2000</p>
          </div>
          
          {/* Image Upload */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Add Photos (Optional)
            </Label>
            <div className="flex flex-wrap gap-3">
              {images.map((img, idx) => (
                <div key={img.id || img.name || `${img.title || "img"}-${idx}`} className="relative">
                  <img 
                    src={img} 
                    alt={`Upload ${idx + 1}`}
                    className="w-20 h-20 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {images.length < 3 && (
                <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  {uploading ? (
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  ) : (
                    <Upload className="h-6 w-6 text-gray-400" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-gray-400">Max 3 images, 5MB each</p>
          </div>
          
          {/* Submit */}
          <Button 
            type="submit" 
            className="w-full" 
            size="lg"
            disabled={ratings.overall === 0 || submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              'Submit Review'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export { ReviewSubmissionWidget, StarRatingInput };
export default ReviewSubmissionWidget;
